import type { ModulesAndAgendasState } from '../index';
import type { LocalFormItemTab } from 'types/agendaForm.ts';
import { generateGridItems, recalcSectionItems } from 'utils/section/items.ts';
import { findSectionIndex, findTabIndex } from 'utils/section/tabSection.ts';
import { toast } from 'react-toastify';
import { getRange, shiftCoord, findLastEmptyIndex, findFirstEmptyIndex, shiftCoordUp } from 'utils/section/gridUtils';
import i18next from 'i18next';

interface Payload {
  name: string;
  currentTab: number;
  direction: 'row' | 'col';
  change: 1 | -1;
  fromStart?: boolean;
}

const getOccupiedIndices = (tabs: LocalFormItemTab[], position: 'x' | 'y'): Set<number> => {
  return new Set(
    tabs
      .flatMap((tab) => tab.items)
      .filter((item) => !item.isEmpty && item[position] !== undefined)
      .flatMap((item) => {
        const { start, end } = getRange(item[position]!);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
      })
  );
};

export const setSectionGrid = (state: ModulesAndAgendasState, { payload }: { payload: Payload }) => {
  const { name, currentTab, direction, change, fromStart } = payload;

  const tabIndex = findTabIndex(state.tabSections, currentTab);
  if (tabIndex === -1) throw new Error('Invalid tab index');

  const sectionIdx = findSectionIndex(state.tabSections[tabIndex].sections, name);
  if (sectionIdx === -1) throw new Error('Invalid section index');

  const section = state.tabSections[tabIndex].sections[sectionIdx];
  let tabs = [...section.formItemTabs];
  let { gridSizeX, gridSizeY } = section;

  const position = direction === 'col' ? 'x' : 'y';

  if (change === 1) {
    if (fromStart) {
      tabs = tabs.map((tab) => ({
        ...tab,
        items: tab.items.map((item) => {
          if (item.isEmpty) return item;

          const currentVal = item[position] as string;
          const newVal = shiftCoordUp(currentVal, 1);

          return {
            ...item,
            [position]: newVal,
            data: item.data ? { ...item.data, [position]: newVal } : item.data,
          };
        }),
      }));
    }
    if (direction === 'col') {
      gridSizeX += 1;
    } else {
      gridSizeY += 1;
    }
  } else {
    const currentSize = direction === 'col' ? gridSizeX : gridSizeY;

    const occupiedIndices = getOccupiedIndices(tabs, position);

    const indexToDelete = fromStart
      ? findFirstEmptyIndex(currentSize, (index) => occupiedIndices.has(index))
      : findLastEmptyIndex(currentSize, (index) => occupiedIndices.has(index));

    if (indexToDelete === -1) {
      const errorKey = direction === 'col' ? 'form.section.error.remove_col' : 'form.section.error.remove_row';
      toast.error(i18next.t(errorKey));
      return;
    }

    tabs = tabs.map((tab) => ({
      ...tab,
      items: tab.items.map((item) => {
        if (item.isEmpty) return item;

        const currentVal = item[position] as string;
        const newVal = shiftCoord(currentVal, indexToDelete);

        if (currentVal === newVal) return item;

        return {
          ...item,
          [position]: newVal,
          data: item.data ? { ...item.data, [position]: newVal } : item.data,
        };
      }),
    }));

    if (direction === 'col') {
      gridSizeX -= 1;
    } else {
      gridSizeY -= 1;
    }
  }

  if (gridSizeX < 1 || gridSizeY < 1) return;

  const updatedTabs = tabs.map((tab) => ({
    ...tab,
    items: generateGridItems(tab.items, gridSizeX, gridSizeY),
  }));

  state.tabSections[tabIndex].sections[sectionIdx] = {
    ...section,
    gridSizeX,
    gridSizeY,
    items: recalcSectionItems(updatedTabs),
    formItemTabs: updatedTabs,
  };
};
